/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * How the subject is compared: exact compares the whole value; wildcard requires a trailing * and matches anything beginning with the value before it. Wildcard additionally requires the issuer to permit it. Defaults to exact.
 */
export const AdmitWorkloadSubjectFormMatchKind = {
  Exact: "exact",
  Wildcard: "wildcard",
} as const;
/**
 * How the subject is compared: exact compares the whole value; wildcard requires a trailing * and matches anything beginning with the value before it. Wildcard additionally requires the issuer to permit it. Defaults to exact.
 */
export type AdmitWorkloadSubjectFormMatchKind = ClosedEnum<
  typeof AdmitWorkloadSubjectFormMatchKind
>;

/**
 * Form for admitting a workload subject and assigning its agent.
 */
export type AdmitWorkloadSubjectForm = {
  /**
   * The agent whose policy the admitted workload inherits.
   */
  agentId: string;
  /**
   * The issuer identifier of an already-trusted issuer, resolved within the caller's organization. Naming one the caller cannot see is a not-found, not a permission error.
   */
  issuer: string;
  /**
   * How the subject is compared: exact compares the whole value; wildcard requires a trailing * and matches anything beginning with the value before it. Wildcard additionally requires the issuer to permit it. Defaults to exact.
   */
  matchKind?: AdmitWorkloadSubjectFormMatchKind | undefined;
  /**
   * Optional label, for platforms whose subjects are not self-describing.
   */
  name?: string | undefined;
  /**
   * Admit the subject for the selected project alone rather than the whole organization. Requires a caller that names a project; a dashboard session does not. Defaults to false.
   */
  projectScoped?: boolean | undefined;
  /**
   * The sub claim the issuer must assert, stored and compared exactly as supplied.
   */
  subject: string;
  /**
   * Free-form labels for finding the admitted workload in a long list. Flat strings, not key/value pairs. Trimmed and de-duplicated on write, then limited to 40 tags of at most 64 characters each.
   */
  tags?: Array<string> | undefined;
};

/** @internal */
export const AdmitWorkloadSubjectFormMatchKind$outboundSchema: z.ZodMiniEnum<
  typeof AdmitWorkloadSubjectFormMatchKind
> = z.enum(AdmitWorkloadSubjectFormMatchKind);

/** @internal */
export type AdmitWorkloadSubjectForm$Outbound = {
  agent_id: string;
  issuer: string;
  match_kind: string;
  name?: string | undefined;
  project_scoped: boolean;
  subject: string;
  tags?: Array<string> | undefined;
};

/** @internal */
export const AdmitWorkloadSubjectForm$outboundSchema: z.ZodMiniType<
  AdmitWorkloadSubjectForm$Outbound,
  AdmitWorkloadSubjectForm
> = z.pipe(
  z.object({
    agentId: z.string(),
    issuer: z.string(),
    matchKind: z._default(
      AdmitWorkloadSubjectFormMatchKind$outboundSchema,
      "exact",
    ),
    name: z.optional(z.string()),
    projectScoped: z._default(z.boolean(), false),
    subject: z.string(),
    tags: z.optional(z.array(z.string())),
  }),
  z.transform((v) => {
    return remap$(v, {
      agentId: "agent_id",
      matchKind: "match_kind",
      projectScoped: "project_scoped",
    });
  }),
);

export function admitWorkloadSubjectFormToJSON(
  admitWorkloadSubjectForm: AdmitWorkloadSubjectForm,
): string {
  return JSON.stringify(
    AdmitWorkloadSubjectForm$outboundSchema.parse(admitWorkloadSubjectForm),
  );
}
